/**
 * TodayDecisions — «Решения дня»: топ-3 приоритетных действия по текущим
 * аномалиям (decision-support). Загружается по кнопке (вызов нейросети).
 */

import { useState } from 'react';
import { Badge, Button, Icon, Spinner } from '@/shared/ui';
import { apiFetch } from '@/api/client';
import { useAiStore } from '@/state/aiStore';
import { useAiChat } from '@/hooks/api/useAiChat';
import { periodQuery, type Period } from '@/components/ui/PeriodPicker';

interface Decision {
  title: string;
  action: string;
  impact: string;
  priority: 'high' | 'medium' | 'low';
}

const PRIO: Record<Decision['priority'], { tone: string; label: string }> = {
  high: { tone: 'b-danger', label: '1' },
  medium: { tone: 'b-warning', label: '2' },
  low: { tone: 'b-info', label: '3' },
};

export function TodayDecisions({ period }: { period: Period }) {
  const [decisions, setDecisions] = useState<Decision[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const openPanel = useAiStore((s) => s.openPanel);
  const { sendMessage } = useAiChat();

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await apiFetch<{ configured: boolean; decisions: Decision[] }>(
        `/api/ai/today?${periodQuery(period)}`,
      );
      setDecisions(r.decisions ?? []);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const apply = (d: Decision) => {
    openPanel();
    void sendMessage(`Решение дня: «${d.title}». ${d.action}\nПодготовь и обоснуй. Боевой аккаунт — только после апрува.`);
  };

  return (
    <section className="card col" style={{ gap: 12 }}>
      <div className="row spread">
        <h3 className="row" style={{ margin: 0, gap: 8 }}><Icon name="Flag" style={{ color: 'var(--primary)' }} /> Решения дня</h3>
        {decisions === null ? (
          <Button variant="primary" size="sm" icon="Sparkles" disabled={loading} onClick={() => void load()}>
            {loading ? 'Собираю…' : 'Собрать'}
          </Button>
        ) : (
          <Button variant="text" size="sm" icon="Sparkles" disabled={loading} onClick={() => void load()}>
            Обновить
          </Button>
        )}
      </div>

      {decisions === null && !loading && (
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Нейросеть приоритизирует текущие проблемы и предложит топ-3 действия на сегодня — что делать и что это даст.
        </p>
      )}
      {loading && (
        <div className="row center gap8 muted" style={{ fontSize: 13 }}><Spinner /> Анализирую и приоритизирую…</div>
      )}
      {err && <span style={{ color: 'var(--danger-fg)', fontSize: 13 }}>{err}</span>}
      {decisions && !decisions.length && !loading && (
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>Острых проблем нет — можно масштабировать лидеров.</p>
      )}

      {decisions?.map((d, i) => {
        const p = PRIO[d.priority];
        return (
          <div key={i} className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
            <Badge tone={p.tone}>{p.label}</Badge>
            <div className="col grow" style={{ gap: 3 }}>
              <span style={{ fontWeight: 700 }}>{d.title}</span>
              <span className="row" style={{ gap: 7, fontSize: 13.5 }}>
                <Icon name="Settings" className="ic" style={{ color: 'var(--text-muted)' }} /> <span>{d.action}</span>
              </span>
              {d.impact && (
                <span className="row muted" style={{ gap: 7, fontSize: 13 }}>
                  <Icon name="Activity" className="ic" style={{ color: 'var(--text-muted)' }} /> <span>{d.impact}</span>
                </span>
              )}
            </div>
            <Button variant="ai" size="sm" icon="ArrowRight" onClick={() => apply(d)}>В чат</Button>
          </div>
        );
      })}
    </section>
  );
}
